import * as React from "react";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import { useHistory } from "react-router-dom";

import Grid from "src/library/components/Hedron";
import { LikeToDo } from "src/library/components/LikeToDo";
import Table from "src/core/dashboard/components/widgets/table/Table";
import { ListActionsList } from "src/core/lists/components/ListActionsList";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { CreateListContext } from "src/core/shared/components/HeaderContext/CreateListContext";
import { listsTableColumns } from "src/core/shared/components/tables/listsTableColumns";
import {
  useListsApi,
  IList
} from "src/core/lists/api/lists.api";
import {
  FilterParams,
  PaginatedParams
} from "src/core/shared/api";
import { setLists } from "src/core/lists/store/lists.actions";
import type { IStore } from "src/core/shared/store";
import useList from "src/core/lists/hooks/useList";
import { PageProps } from "src/pages/Router/routes";
import { useDrop } from "src/core/drops/hooks";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { usePriceVisibility } from "src/core/prices/hooks/use-price-visibility";
import { defaultPagination } from "src/core/lists/store/lists.reducer";
import useTerminology from "src/core/shared/hooks/useTerminology";
import { useCurrentUser } from "src/core/users/hooks";

const ListsPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const dispatch = useDispatch();
  const { erp } = useMappedState(({ global }: IStore) => ({ erp: global.settings && global.settings.erp.name }));
  const [ { currentRole: role } ] = useCurrentUser();
  const terminology = useTerminology();
  const [ { drop } ] = useDrop();
  const [ listsResponse, getLists ] = useListsApi("GET_DROP_LISTS");
  const [ userAccessibleListsResponse, getUserAccessibleLists ] = useListsApi("GET_USER_ACCESSIBLE_LISTS");
  const history = useHistory();
  const [ { isCreatingList }, { createList } ] = useList();
  const checkUserPermission = useCheckUserPermission();
  const pricesVisible = usePriceVisibility();
  const [ { currentRole } ] = useCurrentUser();
  const isCustomerAdmin = React.useMemo(() => currentRole?.code === "customer-admin", [ currentRole?.code ]);
  const listsData = React.useMemo(() => listsResponse.data ?? userAccessibleListsResponse.data, [ listsResponse.data, userAccessibleListsResponse.data ]);
  const isLoading = React.useMemo(() => listsResponse.pending || userAccessibleListsResponse.pending, [ listsResponse.pending, userAccessibleListsResponse.pending ]);

  const onFetchData = React.useCallback((params: {filters: FilterParams<IList>, pagination: PaginatedParams<IList>}) => {
    if (isCustomerAdmin) {
      getUserAccessibleLists({ params });
    } else {
      if (drop?.id) {
        getLists({
          dropId: drop.id,
          params
        });
      }
    }
  }, [
    drop?.id,
    getLists,
    getUserAccessibleLists,
    isCustomerAdmin
  ]);

  React.useEffect(() => {
    if (listsData?.result) {
      dispatch(setLists(listsData.result));
    }
  }, [ dispatch, listsData?.result ]);

  const tableColumns = React.useMemo(() => {
    return listsTableColumns({
      actionButton: info => {
        if (role && erp) {
          return (
            <ListActionsList
              list={info.row.original}
              role={role}
              terminology={props.terminology}
              erp={erp}
            />
          );
        }

        return null;
      },
      pricesVisible,
      adminView: isCustomerAdmin,
      terminology: props.terminology
    }) ;
  }, [
    erp,
    isCustomerAdmin,
    pricesVisible,
    props.terminology,
    role
  ]);

  return (
    <Layout {...props}>
      <MainContentWrapper
        fill
        Header={checkUserPermission("create-list") && (
          <>
            <LikeToDo
              copy="What would you like to do?"
              buttons={[
                <CreateListContext
                  key="create-list-button"
                  createList={createList}
                  loading={isCreatingList}
                  drop={drop}
                />
              ]}
            />
          </>
        )}
      >
        <Grid.Bounds direction="vertical">
          <Grid.Box>
            {tableColumns.length && (
              <Table
                tableOf={terminology?.list.plural.text || "Lists"}
                className="large"
                data={listsData?.result ? listsData.result : []}
                onFetchData={onFetchData}
                defaultPagination={defaultPagination}
                defaultVisibility={{
                  createdAt: false,
                  updatedAt: false
                }}
                loading={isLoading}
                pageCount={listsData?.pages}
                rowCount={listsData?.count}
                error={listsResponse.error ?? userAccessibleListsResponse.error}
                columns={tableColumns}
                mainAction={row => history.push(`/lists/${row.original.id}`)}
              />
            )}
          </Grid.Box>
        </Grid.Bounds>
      </MainContentWrapper>
    </Layout>
  );
};

export default ListsPage;
